import { useCallback, useEffect, useState } from "react";

import { useAppState } from "../../state";

import FeatureFlagList from "./FeatureFlagList";

import {
  Button,
  Card,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import { getFeatureFlags, saveFeatureFlags } from "./FeatureFlagApi";

export default function ToggleFeatureFlagsPage() {
  const [isLoading, setIsLoading] = useState(true);

  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [saveSucceed, setSaveSucceed] = useState(false);

  const [errorMessage, setErrorMessage] = useState<(string | null)>("");
  const [cardCSSClass, setCardCSSClasses] = useState("");

  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);

  const { onAppStateRefresh } = useAppState();

  useEffect(() => {
    getFeatureFlags()
      .then(setFeatureFlags)
      .catch(e => console.log(e))
      .finally(() => setIsLoading(false));
  }, []);

  const onToggleEnabled = useCallback((id: number, enabled: boolean) => {
    setFeatureFlags((current) =>
      current.map((flag) => (flag.id === id ? { ...flag, enabled } : flag))
    );
  }, []);

  async function onSaveClick(): Promise<boolean> {
    try {
      setSaveSucceed(false);
      setIsSaveLoading(true);

      const response = await saveFeatureFlags(featureFlags);
      let responseBody = await response.json();
      switch (response.status) {
        case 400:
          setErrorMessage(responseBody.message ?? "Error saving feature flags");
          setCardCSSClasses("form-group has-error");
          break;
        case 200:
          setErrorMessage(null);
          setCardCSSClasses("");
          if (responseBody.successful === true) {
            onAppStateRefresh();
          }
          setSaveSucceed(true);
          break;
        default:
          break;
      }
      setIsSaveLoading(false);
      return response.ok;
    }
    catch (e) {
      console.log(e);
    }
    setIsSaveLoading(false);
    return false;
  }

  let errorMessageContent;
  if (errorMessage && errorMessage.length > 0) {
    errorMessageContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <label className="error">{errorMessage}</label>
        </Col>
      </Row>
    );
  }

  let succeedMessageContent;
  if (saveSucceed) {
    succeedMessageContent = (
      <Row>
        <Col className="d-flex justify-content-center">
          <label className="success">Feature flags saved successfully</label>
        </Col>
      </Row>
    );
  }

  let saveButtonContent = (
    <>
      <i className="fa fa-save"></i>
      {" "}Save
    </>
  );

  if (isSaveLoading) {
    saveButtonContent = (
      <>
        <Spinner animation="border" size="sm" />
        <span> Saving... </span>
      </>
    );
  }

  const pageTitleContent = (
    <Card.Header>
      <p>Enable one or more feature flag(s) by turning on and off the switch near each flag.<br />
        Note: Changing the feature flags will require you to log out from the application, close the browser window and all tabs, then open a new browser window for the new setting to get in effect</p>
    </Card.Header>
  );

  return (
    <Card className={`table-with-switches bg-white border-0 flagCard ${cardCSSClass}`} id="tableCard">
      {pageTitleContent}
      <Card.Body>
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" aria-label="Loading feature flags" />
          </div>
        ) : (
          <FeatureFlagList featureFlags={featureFlags} onToggleEnabled={onToggleEnabled} />
        )}
        <Row>
          <Col className="d-flex finalColumn">
            <Button className="mr-1" variant="primary" type="button">
              <i className="fa fa-plus"></i>
              {" "}Add Flag
            </Button>
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer>
        <Row>
          <Col className="d-flex finalColumn">
            <Button className="mr-1" variant="primary" onClick={!isSaveLoading ? onSaveClick : () => { }}>
              {saveButtonContent}
            </Button>
          </Col>
        </Row>
        {errorMessageContent}
        {succeedMessageContent}
      </Card.Footer>
    </Card>
  );
}
